import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO, isPast } from 'date-fns';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Loader2, Search, X, RotateCcw, RefreshCw, Phone, Mail, Calendar, Users, Clock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const TASK_TYPES = [
  { value: 'call', label: 'Call', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'meeting', label: 'Meeting', icon: Calendar },
  { value: 'other', label: 'Other', icon: Users },
];

const PRIORITIES = [
  { value: 'high', label: 'High', color: 'bg-red-100 text-red-700' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'low', label: 'Low', color: 'bg-slate-100 text-slate-700' },
];

const STATUSES = [
  { value: 'pending', label: 'Pending', color: 'bg-blue-100 text-blue-700' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-purple-100 text-purple-700' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-slate-100 text-slate-700' },
];

const initialFormData = {
  title: '',
  description: '',
  task_type: 'call',
  lead_id: '',
  due_date: '',
  priority: 'medium',
  status: 'pending',
};

export default function FranchiseTasksPage() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (forceRefresh = false) => {
    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const [tasksRes, leadsRes] = await Promise.all([
        axios.get(`${API}/franchise/tasks`),
        axios.get(`${API}/franchise/leads?limit=1000`)
      ]);
      
      setTasks(tasksRes.data || []);
      setLeads(leadsRes.data.items || []);
      
      if (forceRefresh) toast.success('Data refreshed');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. You need to be in the Franchise team.');
        navigate('/app');
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleReset = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setTypeFilter('all');
    toast.success('Filters cleared');
  };

  const handleCreate = async () => {
    if (!formData.title.trim()) {
      toast.error('Task title is required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/franchise/tasks`, formData);
      toast.success('Task created');
      setCreateDialogOpen(false);
      setFormData(initialFormData);
      loadData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleComplete = async (task) => {
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    try {
      await axios.put(`${API}/franchise/tasks/${task.id}`, { status: newStatus });
      toast.success(newStatus === 'completed' ? 'Task completed!' : 'Task reopened');
      loadData(true);
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDelete = async () => {
    if (!selectedTask) return;
    try {
      await axios.delete(`${API}/franchise/tasks/${selectedTask.id}`);
      toast.success('Task deleted');
      setDeleteDialogOpen(false);
      loadData(true);
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const getStatusBadge = (status) => {
    const s = STATUSES.find(x => x.value === status) || STATUSES[0];
    return <Badge className={s.color}>{s.label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const p = PRIORITIES.find(x => x.value === priority) || PRIORITIES[1];
    return <Badge className={p.color}>{p.label}</Badge>;
  };

  const getTaskTypeIcon = (type) => {
    const t = TASK_TYPES.find(x => x.value === type);
    const Icon = t?.icon || Users;
    return <Icon className="h-4 w-4" />;
  };

  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      if (statusFilter !== 'all' && task.status !== statusFilter) return false;
      if (typeFilter !== 'all' && task.task_type !== typeFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const searchFields = [task.title, task.description, task.lead_name].filter(Boolean).join(' ').toLowerCase();
        if (!searchFields.includes(q)) return false;
      }
      return true;
    });
  }, [tasks, statusFilter, typeFilter, searchQuery]);

  const pendingTasks = tasks.filter(t => t.status === 'pending' || t.status === 'in_progress');
  const overdueTasks = pendingTasks.filter(t => t.due_date && isPast(parseISO(t.due_date)));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Franchise Tasks</h1>
          <p className="text-slate-500 mt-1">Manage follow-ups and action items</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} data-testid="add-franchise-task-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Task
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{tasks.length}</p>
            <p className="text-sm text-slate-500">Total Tasks</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{pendingTasks.length}</p>
            <p className="text-sm text-slate-500">Pending</p>
          </CardContent>
        </Card>
        <Card className={overdueTasks.length > 0 ? 'border-red-300' : ''}>
          <CardContent className="p-4 text-center">
            <p className={`text-2xl font-bold ${overdueTasks.length > 0 ? 'text-red-600' : 'text-slate-400'}`}>
              {overdueTasks.length}
            </p>
            <p className="text-sm text-slate-500">Overdue</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">
              {tasks.filter(t => t.status === 'completed').length}
            </p>
            <p className="text-sm text-slate-500">Completed</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10"
            data-testid="search-franchise-tasks"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="h-4 w-4 text-slate-400 hover:text-slate-600" />
            </button>
          )}
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {TASK_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={handleReset} title="Reset filters">
          <RotateCcw className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="icon" onClick={() => loadData(true)} disabled={refreshing} title="Refresh">
          {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {/* Tasks Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="w-10 p-3"></th>
                  <th className="text-left p-3 font-medium text-slate-700">Task</th>
                  <th className="text-left p-3 font-medium text-slate-700">Type</th>
                  <th className="text-left p-3 font-medium text-slate-700">Lead</th>
                  <th className="text-left p-3 font-medium text-slate-700">Due Date</th>
                  <th className="text-left p-3 font-medium text-slate-700">Priority</th>
                  <th className="text-left p-3 font-medium text-slate-700">Status</th>
                  <th className="w-24 p-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-slate-500">
                      {searchQuery || statusFilter !== 'all' || typeFilter !== 'all'
                        ? 'No tasks match your filters'
                        : 'No tasks yet. Click "Add Task" to create one.'}
                    </td>
                  </tr>
                ) : (
                  filteredTasks.map(task => {
                    const isOverdue = task.due_date && task.status !== 'completed' && isPast(parseISO(task.due_date));
                    return (
                      <tr key={task.id} className={`border-b hover:bg-slate-50 ${task.status === 'completed' ? 'opacity-60' : ''}`}>
                        <td className="p-3">
                          <Checkbox
                            checked={task.status === 'completed'}
                            onCheckedChange={() => handleToggleComplete(task)}
                          />
                        </td>
                        <td className="p-3">
                          <div className={`font-medium ${task.status === 'completed' ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                            {task.title}
                          </div>
                          {task.description && (
                            <div className="text-sm text-slate-500 truncate max-w-xs">{task.description}</div>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {getTaskTypeIcon(task.task_type)}
                            <span className="text-sm capitalize">{task.task_type}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          {task.lead_name ? (
                            <button
                              className="text-sm text-blue-600 hover:underline"
                              onClick={() => navigate(`/admin/franchise/leads/${task.lead_id}`)}
                            >
                              {task.lead_name}
                            </button>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="p-3">
                          {task.due_date ? (
                            <div className={`flex items-center gap-1 text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-600'}`}>
                              {isOverdue && <AlertCircle className="h-3 w-3" />}
                              {format(parseISO(task.due_date), 'MMM d, yyyy')}
                            </div>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="p-3">{getPriorityBadge(task.priority)}</td>
                        <td className="p-3">{getStatusBadge(task.status)}</td>
                        <td className="p-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => { setSelectedTask(task); setDeleteDialogOpen(true); }}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Task Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-indigo-600" />
              Create Franchise Task
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Task Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Follow up with prospect"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Task details..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Task Type</Label>
                <Select value={formData.task_type} onValueChange={(val) => setFormData({ ...formData, task_type: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TASK_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(val) => setFormData({ ...formData, priority: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Link to Lead</Label>
                <Select value={formData.lead_id} onValueChange={(val) => setFormData({ ...formData, lead_id: val })}>
                  <SelectTrigger><SelectValue placeholder="Select lead (optional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">No lead</SelectItem>
                    {leads.slice(0, 50).map(l => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.first_name} {l.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedTask?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
